import { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
// Razorpay Key ID (public key — safe to use client-side)
// Find it: Razorpay Dashboard → Settings → API Keys
var RAZORPAY_KEY_ID = "rzp_live_SX3HRLGzZVfdUo"; // e.g. "rzp_live_xxxxxxxxxx"

// Formspree endpoint — sends you an email with the customer's booking details.
// 1. Sign up free at https://formspree.io → Create a new form
// 2. Copy the endpoint and paste it below.
var FORMSPREE_ENDPOINT = "https://formspree.io/f/mqegplwr";
// ─────────────────────────────────────────────────────────────────────────────

var FEATURES = [
  { icon: "🖥️", title: "Up to 5 Pages",         desc: "Home, About, Services, Contact + 1 custom page of your choice." },
  { icon: "📱", title: "Fully Responsive",        desc: "Pixel-perfect on mobile, tablet, and desktop — no exceptions." },
  { icon: "⚡", title: "Fast & Optimised",        desc: "Optimised assets, clean code, and quick load times out of the box." },
  { icon: "🎨", title: "Custom Design",           desc: "Unique design tailored to your brand — not a template." },
  { icon: "📬", title: "Contact Form",            desc: "Working contact form so leads can reach you instantly." },
  { icon: "🔒", title: "SSL & Secure Hosting",   desc: "HTTPS and reliable hosting setup included for the first month." },
  { icon: "🔎", title: "Basic SEO",               desc: "Meta tags, Open Graph, and sitemap to get you indexed on Google." },
  { icon: "🚀", title: "Live in 7–10 Days",       desc: "Fast turnaround — we deliver within 7 to 10 business days." },
];

var STEPS = [
  { step: "01", title: "Book & Pay",       desc: "Fill the form below and complete payment. You'll receive a confirmation email." },
  { step: "02", title: "Discovery Call",   desc: "We schedule a 30-min call to understand your brand, goals, and content." },
  { step: "03", title: "Design & Build",   desc: "Our team designs and develops your site — you review drafts at every stage." },
  { step: "04", title: "Launch",           desc: "Final approval, domain setup, and your site goes live within the agreed timeline." },
];

function useScrollReveal() {
  useEffect(function () {
    var elements = document.querySelectorAll(".reveal");
    if (!elements.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    elements.forEach(function (el) { observer.observe(el); });
    return function () { observer.disconnect(); };
  }, []);
}

function BasicWebsite() {
  useScrollReveal();
  var history = useHistory();

  useEffect(function () {
    window.scrollTo(0, 0);
  }, []);

  // Load Razorpay checkout.js once when the page mounts
  useEffect(function () {
    if (document.getElementById("razorpay-checkout-js")) return;
    var script = document.createElement("script");
    script.id  = "razorpay-checkout-js";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  var [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    notes: "",
  });
  var [errors, setErrors] = useState({});
  var [submitted, setSubmitted] = useState(false);
  var [paymentError, setPaymentError] = useState("");

  function handleChange(e) {
    var field = e.target.name;
    var value = e.target.value;
    setForm(function (prev) { return Object.assign({}, prev, { [field]: value }); });
    if (errors[field]) {
      setErrors(function (prev) { return Object.assign({}, prev, { [field]: "" }); });
    }
  }

  function validate() {
    var errs = {};
    if (!form.name.trim())                              errs.name     = "Name is required.";
    if (!form.email.trim())                             errs.email    = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email.";
    if (!form.phone.trim())                             errs.phone    = "Phone number is required.";
    else if (!/^\+?[0-9\s\-()]{7,15}$/.test(form.phone))     errs.phone = "Enter a valid phone number.";
    return errs;
  }

  function handleSubmit(e) {
    e.preventDefault();
    var errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    // 1. Send booking details to Formspree so you get an email notification
    fetch(FORMSPREE_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({
        name:     form.name.trim(),
        email:    form.email.trim(),
        phone:    form.phone.trim(),
        business: form.business.trim(),
        notes:    form.notes.trim(),
        package:  "Basic Website — ₹15,999",
      }),
    }).catch(function () {
      // Silently ignore — don't block the user from paying if notification fails
    });

    // 2. Open Razorpay Checkout modal with prefilled customer details
    // Normalise phone: strip non-digits, remove leading 91 country code → 10 digits
    var rawPhone = form.phone.trim().replace(/\D/g, "");
    var contact  = rawPhone.startsWith("91") && rawPhone.length === 12
      ? rawPhone.slice(2)
      : rawPhone;

    var options = {
      key:         RAZORPAY_KEY_ID,
      amount:      1599900, // ₹15,999 in paise (100 paise = ₹1)
      currency:    "INR",
      name:        "Nuevo Alture Tech Ventures",
      description: "Basic Website Package — Up to 5 Pages",
      prefill: {
        name:    form.name.trim(),
        email:   form.email.trim(),
        contact: contact,
      },
      notes: {
        business:     form.business.trim() || "N/A",
        requirements: form.notes.trim()    || "N/A",
      },
      theme: {
        color: "#00aeef",
      },
      handler: function (response) {
        // Payment successful — navigate to success page with payment ID
        history.push(
          "/payment-success?razorpay_payment_id=" + response.razorpay_payment_id
        );
      },
    };

    var rzp = new window.Razorpay(options);

    rzp.on("payment.failed", function (response) {
      setPaymentError(response.error.description || "Payment failed. Please try again.");
    });

    rzp.open();
  }

  return (
    <div className={"bw-page"}>

      {/* ─── Hero ────────────────────────────────────────────────────────── */}
      <section className={"bw-hero"}>
        <div className={"bw-hero-inner"}>
          <span className={"bw-hero-badge"}>
            <span className={"badge-dot"}></span>
            Basic Website Package
          </span>
          <h1 className={"bw-hero-title"}>
            Your Business,<br />
            <span className={"bw-hero-gradient"}>Online in Days.</span>
          </h1>
          <p className={"bw-hero-sub"}>
            A professionally designed, fully responsive website — up to 5 pages —
            built and delivered within 10 business days. Perfect for businesses,
            freelancers, and creators who need a powerful online presence fast.
          </p>

          <div className={"bw-price-card"}>
            <div className={"bw-price-label"}>All-inclusive price</div>
            <div className={"bw-price-amount"}>
              <span className={"bw-price-currency"}>₹</span>
              <span className={"bw-price-number"}>15,999</span>
              <span className={"bw-price-gst"}>+ GST</span>
            </div>
            <div className={"bw-price-note"}>One-time · No hidden charges · No monthly fees</div>
          </div>

          <a href={"#book"} className={"btn-primary bw-hero-cta"}>
            Book Now
          </a>
        </div>

        <div className={"bw-hero-scroll"}>
          <span className={"scroll-line"}></span>
          <span>Scroll to see what's included</span>
        </div>
      </section>

      {/* ─── What's Included ─────────────────────────────────────────────── */}
      <section className={"bw-features"}>
        <div className={"bw-features-inner"}>
          <div className={"bw-section-header reveal"}>
            <span className={"u-label"}>What you get</span>
            <h2>EVERYTHING YOU NEED</h2>
            <p>
              No piecemeal add-ons. One flat price covers design, development,
              basic SEO, and a month of hosting — ready to go live.
            </p>
          </div>

          <div className={"bw-features-grid"}>
            {FEATURES.map(function (f, i) {
              return (
                <div
                  className={"bw-feature-card reveal reveal-delay-" + ((i % 4) + 1)}
                  key={i}
                >
                  <span className={"bw-feature-icon"}>{f.icon}</span>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── How It Works ────────────────────────────────────────────────── */}
      <section className={"bw-steps"}>
        <div className={"bw-steps-inner"}>
          <div className={"bw-section-header reveal"}>
            <span className={"u-label"}>The process</span>
            <h2>HOW IT WORKS</h2>
          </div>

          <div className={"bw-steps-grid"}>
            {STEPS.map(function (s, i) {
              return (
                <div
                  className={"bw-step-card reveal reveal-delay-" + (i + 1)}
                  key={i}
                >
                  <span className={"bw-step-num"}>{s.step}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Booking Form ────────────────────────────────────────────────── */}
      <section className={"bw-book"} id={"book"}>
        <div className={"bw-book-inner"}>
          <div className={"bw-book-left reveal"}>
            <span className={"u-label"}>Book your spot</span>
            <h2>LET'S GET STARTED</h2>
            <p>
              Fill in your details and click "Proceed to Pay". You'll be taken
              securely to Razorpay's payment page to complete the ₹15,999 payment.
              We'll reach out within 24 hours of confirmation.
            </p>

            <div className={"bw-trust-items"}>
              <div className={"bw-trust-item"}>
                <span className={"bw-trust-icon"}>🔒</span>
                <span>Secure payment via Razorpay</span>
              </div>
              <div className={"bw-trust-item"}>
                <span className={"bw-trust-icon"}>📧</span>
                <span>Instant confirmation email</span>
              </div>
              <div className={"bw-trust-item"}>
                <span className={"bw-trust-icon"}>💬</span>
                <span>We respond within 24 hours</span>
              </div>
              <div className={"bw-trust-item"}>
                <span className={"bw-trust-icon"}>🏆</span>
                <span>9+ years · 12+ clients · 100% satisfaction</span>
              </div>
            </div>

            <div className={"bw-contact-alt"}>
              <p>Have questions before booking?</p>
              <a href={"mailto:contact@nuevoalture.com"} className={"btn-ghost"}>
                Email Us First
              </a>
            </div>
          </div>

          <div className={"bw-book-right reveal reveal-delay-2"}>
            <div className={"bw-form-card"}>
              <div className={"bw-form-header"}>
                <h3>Basic Website — ₹15,999</h3>
                <p>Upto 5 pages · Delivered in 7–10 days</p>
              </div>

              {paymentError && (
                <div className={"bw-payment-error"}>{paymentError}</div>
              )}
              {false ? null : (
                <form onSubmit={handleSubmit} noValidate>
                  <div className={"bw-form-group"}>
                    <label htmlFor={"bw-name"}>Full Name *</label>
                    <input
                      id={"bw-name"}
                      type={"text"}
                      name={"name"}
                      value={form.name}
                      onChange={handleChange}
                      placeholder={"e.g. Ramesh Kumar"}
                      className={errors.name ? "error" : ""}
                    />
                    {errors.name && <span className={"bw-field-error"}>{errors.name}</span>}
                  </div>

                  <div className={"bw-form-group"}>
                    <label htmlFor={"bw-email"}>Email Address *</label>
                    <input
                      id={"bw-email"}
                      type={"email"}
                      name={"email"}
                      value={form.email}
                      onChange={handleChange}
                      placeholder={"you@example.com"}
                      className={errors.email ? "error" : ""}
                    />
                    {errors.email && <span className={"bw-field-error"}>{errors.email}</span>}
                  </div>

                  <div className={"bw-form-group"}>
                    <label htmlFor={"bw-phone"}>Phone Number *</label>
                    <input
                      id={"bw-phone"}
                      type={"tel"}
                      name={"phone"}
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={"+91 98765 43210"}
                      className={errors.phone ? "error" : ""}
                    />
                    {errors.phone && <span className={"bw-field-error"}>{errors.phone}</span>}
                  </div>

                  <div className={"bw-form-group"}>
                    <label htmlFor={"bw-business"}>Business / Brand Name</label>
                    <input
                      id={"bw-business"}
                      type={"text"}
                      name={"business"}
                      value={form.business}
                      onChange={handleChange}
                      placeholder={"Your business name (optional)"}
                    />
                  </div>

                  <div className={"bw-form-group"}>
                    <label htmlFor={"bw-notes"}>Brief about your website</label>
                    <textarea
                      id={"bw-notes"}
                      name={"notes"}
                      value={form.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder={"What kind of website do you need? Any specific pages or features? (optional)"}
                    />
                  </div>

                  <button type={"submit"} className={"bw-pay-btn"}>
                    <span>Proceed to Pay — ₹15,999</span>
                    <span className={"bw-pay-arrow"}>→</span>
                  </button>

                  <p className={"bw-form-disclaimer"}>
                    By proceeding you agree to our{" "}
                    <Link to={"/"}>Terms of Service</Link>. Payment is processed
                    securely by Razorpay Payments.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─────────────────────────────────────────────────────────── */}
      <section className={"bw-faq"}>
        <div className={"bw-faq-inner"}>
          <div className={"bw-section-header reveal"}>
            <span className={"u-label"}>FAQ</span>
            <h2>COMMON QUESTIONS</h2>
          </div>
          <div className={"bw-faq-grid"}>
            {[
              { q: "What do I need to provide?",         a: "Your logo (or we can help create one), brand colours, content/text for each page, and any reference websites you like. Don't worry — we guide you through a checklist after booking." },
              { q: "Can I add more than 5 pages?",       a: "Yes! Additional pages can be added at ₹1,999 per page. We'll discuss this during the discovery call if you need them." },
              { q: "Is hosting included?",               a: "We cover the first month of hosting setup. After that, a standard hosting plan (typically ₹1,500–₹3,000/year) applies — we'll help you set it up." },
              { q: "What if I need changes after launch?", a: "Two rounds of revisions are included before launch. Post-launch changes are billed at our standard hourly rate, or you can opt into our maintenance plan." },
              { q: "What payment methods are accepted?", a: "All major UPI apps (GPay, PhonePe, Paytm), credit/debit cards, net banking — via Razorpay's secure payment gateway." },
              { q: "Do you offer a refund?",             a: "If you cancel within 24 hours of payment and before the discovery call, we issue a full refund. After work begins, refunds are not available." },
            ].map(function (faq, i) {
              return (
                <div className={"bw-faq-item reveal reveal-delay-" + ((i % 3) + 1)} key={i}>
                  <h4>{faq.q}</h4>
                  <p>{faq.a}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
}

export default BasicWebsite;
